import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import { Command, ProcessStatus, Role } from 'constants/constants';
import { COLLABORATION_REQUEST_SENT_TEXT } from 'components/v2/compiler/constants';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import { Button } from 'primereact/button';
import { ActionState, CountryModel, DeveloperCompanyModel, MembershipModel } from 'services/EpdClient';
import { CompilerCompositeStatusModel, EPDDeveloperModel, Option } from 'types/types';
import { MultiValue } from 'react-select';
import _ from 'lodash';
import MembershipService from 'services/MembershipService';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import LcaPractitionersSelectorDialog from './LcaPractitionersSelectorDialog';
import { useConnectEpdLcaPractitioner, useRequestCollaboration } from 'services/api/mutations';
import { FindButtonBox } from 'components/v2/form/styled';
import { toaster } from 'components/v2/toast';
import CompanyService from 'services/CompanyService';
import AuthService from 'services/AuthService';
import { toast } from 'react-toastify';
import { usePractitionerMarketplaceCountriesLookup } from 'hooks/lookups';
import axios, { AxiosError } from 'axios';

interface LcaPractitionersSelectorProps {
  epdId: string;
  epdVersionId: string;
  practitioners?: EPDDeveloperModel[] | undefined;
  onChanged: any;
  validationState: CompilerCompositeStatusModel;
}

const LcaPractitionersSelector: React.FunctionComponent<LcaPractitionersSelectorProps> = ({
  epdId,
  epdVersionId,
  practitioners,
  onChanged,
  validationState,
}) => {
  const { companyId } = useContext(CompanyContext);
  const history = useHistory();
  const [showSelectDialog, setShowSelectDialog] = useState<boolean>(false);
  const [epdDeveloperCompanies, setEpdDeveloperCompanies] = useState<DeveloperCompanyModel[]>([]);
  const [epdDevelopers, setEpdDevelopers] = useState<MembershipModel[]>([]);
  const [inquiryStatus, setInquiryStatus] = useState<[ProcessStatus, Command]>([ProcessStatus.None, Command.None]);
  const [filteredEpdDeveloperCompanies, setFilteredEpdDeveloperCompanies] = useState<DeveloperCompanyModel[]>([]);
  const [selectedEpdDeveloperCompany, setSelectedEpdDeveloperCompany] = useState<DeveloperCompanyModel | undefined>();
  const [selectedCountry, setSelectedCountry] = useState<Option | undefined>();
  const connectLcaPractitionerMutation = useConnectEpdLcaPractitioner(epdVersionId, (error) => {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ detail?: string }>;

      if (axiosError.response?.status === 403) {
        if (axiosError.response.data?.detail === 'You do not have permission to view this EPD.') {
          toaster({
            severity: 'error',
            summary: 'Access denied',
            details: 'You do not have permission to view this EPD.',
          });

          history.push(EpdLinks.dashboard());
          return;
        }

        toaster({
          severity: 'error',
          summary: 'Error',
          details: 'Only the EPD Owner is allowed to perform this action.',
        });

        return;
      }
    }

    setFilteredEpdDeveloperCompanies((prev) =>
      prev.map((c) => (c.id === selectedEpdDeveloperCompany?.id ? { ...c, errorText: 'Something went wrong.' } : c))
    );
  });

  const requestCollaborationMutation = useRequestCollaboration(epdVersionId!);
  const countries = usePractitionerMarketplaceCountriesLookup().data || [];

  const fetchEpdDevelopers = async () => {
    const developers = await MembershipService.getMembershipsByCompanyAndRole(companyId!, Role.EPDDeveloper);
    const owners = await MembershipService.getMembershipsByCompanyAndRole(companyId!, Role.EPDOwner);
    setEpdDevelopers([...developers, ...owners]);
  };

  useEffect(() => {
    fetchEpdDevelopers();
  }, [companyId]);

  useEffect(() => {
    const fetchEpdDeveloperCompanies = async () => {
      const res = await CompanyService.getDevelopers(undefined, selectedCountry?.value, epdVersionId);
      const companies = [...res].sort((a, b) => ((a.name ?? '') > (b.name ?? '') ? 1 : -1));
      setEpdDeveloperCompanies(companies);
      setFilteredEpdDeveloperCompanies(companies);
    };

    fetchEpdDeveloperCompanies();
  }, [selectedCountry]);

  const handleCloseDialog = () => {
    setShowSelectDialog(false);
    setEpdDeveloperCompanies([]);
  };

  const handleSelectPcr = () => {
    handleCloseDialog();
  };

  const changeEpdDevelopers = (_: string, newDevelopersUserIds: string[]) => {
    const oldDevelopersUserIds = (practitioners ?? []).map((d) => d.contactUserId);
    const removedSelf = handleSelfRemovingFromDevelopers(oldDevelopersUserIds, newDevelopersUserIds);
    if (removedSelf) {
      return;
    }

    const newDevelopersMembershipIds = epdDevelopers.filter((x) => newDevelopersUserIds.includes(x.userId)).map((x) => x.id);
    onChanged('EPDDevelopers', newDevelopersMembershipIds);
  };

  const handleSelfRemovingFromDevelopers = (oldDevelopersIds: string[], newDevelopersIds: string[]): boolean => {
    const removedDevelopers = oldDevelopersIds.filter((old) => !newDevelopersIds.includes(old));
    const currentUserId = AuthService.getUser()!.id;
    const removedSelf = removedDevelopers.includes(currentUserId);
    if (!removedSelf) {
      return false;
    }

    const isAdmin = AuthService.isAdmin();
    const isLicenseeAdmin = AuthService.isLicenseeAdmin();
    const isOwner = AuthService.isCompanyOwner(companyId!);
    if (isAdmin || isLicenseeAdmin || isOwner) {
      return false;
    }

    toast.error('You cannot delete your own entry from the EPD. Contact the EPD Owner for further action.');
    return true;
  };

  const onAddExternalPractitioner = async (membershipId: string) => {
    try {
      setInquiryStatus([ProcessStatus.Fetching, Command.Connect]);
      await connectLcaPractitionerMutation.mutateAsync({ epdId, membershipId });
      setInquiryStatus([ProcessStatus.Success, Command.Connect]);
      fetchEpdDevelopers();
      toaster({
        severity: 'success',
        summary: 'The LCA Practitioner was added to the EPD!',
        details: 'Now the LCA Practitioner has an access to the EPD.',
      });
    } catch (error) {
      setInquiryStatus([ProcessStatus.Error, Command.Connect]);
    }
  };

  const onRequestCollaboration = async (companyId: string) => {
    try {
      setInquiryStatus([ProcessStatus.Fetching, Command.Inquiry]);
      await requestCollaborationMutation.mutateAsync({
        epdVersionId,
        roleId: Role.EPDDeveloper,
        companyId,
      });
      setInquiryStatus([ProcessStatus.Success, Command.Inquiry]);
      toaster({
        severity: 'success',
        summary: 'Your collaboration request has been sent.',
        details: 'Please wait for a confirmation response.',
      });
      setFilteredEpdDeveloperCompanies((prev) =>
        prev.map((c) =>
          c.id === selectedEpdDeveloperCompany?.id ? { ...c, successOperationText: COLLABORATION_REQUEST_SENT_TEXT } : c
        )
      );
    } catch (error) {
      setInquiryStatus([ProcessStatus.Error, Command.Inquiry]);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ detail?: string }>;
        if (axiosError.response?.status === 403) {
          if (axiosError.response.data?.detail === 'You do not have permission to view this EPD.') {
            toaster({
              severity: 'error',
              summary: 'Access denied',
              details: 'You do not have permission to view this EPD.',
            });
            history.push(EpdLinks.dashboard());
            return;
          }
          toaster({
            severity: 'error',
            summary: 'Error',
            details: 'Only the EPD Owner is allowed to perform this action.',
          });
          return;
        }
      }
      setFilteredEpdDeveloperCompanies((prev) =>
        prev.map((c) => (c.id === selectedEpdDeveloperCompany?.id ? { ...c, errorText: 'Something went wrong.' } : c))
      );
    }
  };

  const handleSearchVerifierCompanies = (event: any) => {
    const searchTerm = event.target.value.toLowerCase();
    if (!searchTerm) {
      setFilteredEpdDeveloperCompanies(epdDeveloperCompanies);
      return;
    }
    setFilteredEpdDeveloperCompanies((prev) =>
      epdDeveloperCompanies.filter(
        (item) =>
          item.name?.toLowerCase().includes(searchTerm) ||
          item.members?.some((m) => m.email?.toLowerCase().includes(searchTerm))
      )
    );
  };

  const onChangePractitionerCountry = async (propertyName: string, val: any) => {
    if (!countries.length) return;
    setSelectedCountry(countries.find((c) => c.value === val));
  };

  const selectedPractitioners = practitioners?.map(
    (dev: any) => ({ label: dev.contactName, value: dev.contactUserId } as Option)
  );

  const practitionersOptions = epdDevelopers.map((user) => {
    return {
      value: user.userId,
      label: `${user.userName} (${user.roleName})`,
    } as Option;
  });

  return (
    <>
      <div style={{ flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
        <CompilerSelectControlledComponent
          label="LCA Practitioner"
          tooltip="The person accountable for underlying Life cycle assessment."
          name="epdDevelopers"
          options={practitionersOptions}
          value={selectedPractitioners}
          onChanged={changeEpdDevelopers}
          placeholder="Select"
          error={validationState.errors.lcaPractitioners}
          isMulti
          required
        />
        {validationState.fieldsState.inviteEpdDeveloper === ActionState.Enabled && (
          <FindButtonBox>
            <Button
              style={{ fontSize: 14, fontWeight: 500 }}
              type="button"
              label="+ Find LCA Practitioner"
              text
              onClick={() => setShowSelectDialog(true)}
            />
          </FindButtonBox>
        )}
        <LcaPractitionersSelectorDialog
          epdId={epdId}
          epdVersionId={epdVersionId}
          companyId={companyId}
          selectedPractitioners={selectedPractitioners}
          companyPractitioners={practitionersOptions}
          filteredEpdDeveloperCompanies={filteredEpdDeveloperCompanies}
          handleSearchVerifierCompanies={handleSearchVerifierCompanies}
          inquiryStatus={inquiryStatus}
          isOpened={showSelectDialog}
          onHide={handleCloseDialog}
          onChange={handleSelectPcr}
          onAddPractitioner={onAddExternalPractitioner}
          onRequestCollaboration={onRequestCollaboration}
          onChangePractitionerCountry={onChangePractitionerCountry}
          contentStyle={epdDeveloperCompanies?.length > 0 ? {} : { overflowY: 'initial' }}
          selectedEpdDeveloperCompany={selectedEpdDeveloperCompany}
          setSelectedEpdDeveloperCompany={setSelectedEpdDeveloperCompany}
          countries={countries}
          selectedCountry={selectedCountry}
        />
      </div>
    </>
  );
};

export default LcaPractitionersSelector;
